import { uploadClient } from './network';

export interface PresignedUrlResponse {
  uploadUrl: string;
  downloadUrl: string;
  /**
   * Pre-signed URL that withdraws this file from the server.
   *
   * Handed out with the upload so an attachment the user removes before
   * sending can actually be deleted, rather than sitting on disk until its TTL
   * expires. See `deleteUploadedFile`.
   */
  deleteUrl: string;
  fileId: string;
  expiresAt: number;
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percent: number;
}

/**
 * Request a presigned upload URL from the LLM LB
 */
export async function getPresignedUrl(
  apiUrl: string,
  filename: string,
  mimeType: string,
  size: number
): Promise<PresignedUrlResponse> {
  const { data } = await uploadClient.post<PresignedUrlResponse>(
    `${apiUrl}/files/presign`,
    { filename, mimeType, size }
  );

  return data;
}

/**
 * Upload file data to a presigned URL with progress tracking
 */
export async function uploadToPresignedUrl(
  uploadUrl: string,
  data: ArrayBuffer,
  mimeType: string,
  onProgress?: (progress: UploadProgress) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress({
          loaded: event.loaded,
          total: event.total,
          percent: Math.round((event.loaded / event.total) * 100),
        });
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed: ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Upload failed'));
    });

    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', mimeType);
    xhr.send(data);
  });
}

/**
 * Delete a file the user attached and then removed before sending.
 *
 * Best-effort and deliberately silent on failure: this runs when an attachment
 * is removed or replaced, and there is nothing useful to say to a user who has
 * already moved on. The server's TTL sweep is the backstop if it does not
 * land.
 */
export async function deleteUploadedFile(deleteUrl: string): Promise<void> {
  try {
    await fetch(deleteUrl, { method: 'DELETE', keepalive: true });
  } catch {
    // Backstopped by the server-side TTL.
  }
}

/**
 * Upload a file using presigned URLs.
 *
 * Returns the download URL to put in the message AND the delete URL that
 * withdraws it, because an eagerly-uploaded file the user then removes has to
 * be retractable — otherwise attaching by mistake silently persists their
 * content on a server they believe it never reached.
 */
export async function uploadFile(
  apiUrl: string,
  file: File,
  onProgress?: (progress: UploadProgress) => void
): Promise<{ downloadUrl: string; deleteUrl: string }> {
  // Get presigned URL
  const { uploadUrl, downloadUrl, deleteUrl } = await getPresignedUrl(
    apiUrl,
    file.name,
    file.type || 'application/octet-stream',
    file.size
  );

  // Upload file data.
  //
  // If the PUT fails partway the server may still be holding bytes, and the
  // caller is about to discard the only reference to them — so withdraw before
  // rethrowing rather than leaking whatever landed.
  const data = await file.arrayBuffer();
  try {
    await uploadToPresignedUrl(uploadUrl, data, file.type || 'application/octet-stream', onProgress);
  } catch (err) {
    await deleteUploadedFile(deleteUrl);
    throw err;
  }

  return { downloadUrl, deleteUrl };
}
